import React, { useEffect } from 'react';
import { Routes, Route, Outlet, Navigate, useLocation, Link } from 'react-router-dom';
import { clarityIdentify } from './utils/clarity';
import Header from './components/Header';
import Toast from './components/Toast';
import { ToastProvider } from './context/ToastContext';
import { ConfirmProvider } from './context/ConfirmContext';
import HomePage from './pages/HomePage';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ProductsPage from './pages/ProductsPage';
import AdminPage from './pages/AdminPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ProductDetailPage from './pages/ProductDetailPage';
import ForcePasswordChange from './pages/ForcePasswordChange';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { InternalChatProvider } from './context/InternalChatContext';
import TeamChatWidget from './components/internal-chat/TeamChatWidget';
import SsoLogin from './pages/SsoLogin';
import QuoteCartPage from './pages/QuoteCartPage';
import QuoteViewPage from './pages/QuoteViewPage';
import './App.css';
import ViewToggle from './components/ViewToggle';
import ChatWidget from './components/ChatWidget';
import VerifyEmailPage from './pages/VerifyEmailPage';
import SharedCartPage from './pages/SharedCartPage';
import MyCartsPage from './pages/MyCartsPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import LocalCartPage from './pages/LocalCartPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import McpDocsPage from './pages/McpDocsPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import GalleryPage from './pages/GalleryPage';
const LayoutWithHeader = () => {
  return (
    <>
      <Header />
      <div className="container">
        <Outlet />
      </div>
      <footer style={{ borderTop: '1px solid #eee', padding: '16px 24px', textAlign: 'center', fontSize: 13, color: '#888' }}>
        © {new Date().getFullYear()} eYantra. &nbsp;
        <Link to="/privacy-policy" style={{ color: '#888', textDecoration: 'underline' }}>Privacy Policy</Link>
        &nbsp;·&nbsp;
        <Link to="/terms" style={{ color: '#888', textDecoration: 'underline' }}>Terms of Service</Link>
      </footer>
    </>
  );
};

const UserIdentifier = () => {
  const { user } = useAuth();
  useEffect(() => {
    if (user?.username) clarityIdentify(user.username);
  }, [user]);
  return null;
};

const ProtectedRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading-state">Loading session...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

const AUTH_PATHS = ['/login', '/register', '/verify-email', '/force-password-change',
                    '/sso-login', '/forgot-password', '/reset-password'];

function App() {
  const location = useLocation();
  const showChat = !AUTH_PATHS.some(p => location.pathname.startsWith(p))
    && !location.pathname.startsWith('/gallery');

  return (
    <ThemeProvider>
    <ToastProvider>
    <ConfirmProvider>
    <InternalChatProvider>
      <UserIdentifier />
      <Toast />
      <Routes>
        {/* Landing page — no header, uses its own full-bleed layout */}
        <Route path="/" element={<LandingPage />} />

        {/* Auth routes — no header */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/force-password-change" element={<ForcePasswordChange />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/sso-login" element={<SsoLogin />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Public showcase gallery — no header, no login, shareable permalinks */}
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/gallery/:postId" element={<GalleryPage />} />

        {/* ALL Routes WITH the standard Header */}
        <Route element={<LayoutWithHeader />}>

          {/* Public routes */}
          <Route path="/quote" element={<QuoteViewPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/mcp" element={<McpDocsPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/product/:productId" element={<ProductDetailPage />} />
          <Route path="/cart" element={<LocalCartPage />} />

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<HomePage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/quote-cart" element={<QuoteCartPage />} />
            <Route path="/shared-cart" element={<SharedCartPage />} />
            <Route path="/my-carts" element={<MyCartsPage />} />
          </Route>

        </Route>
      </Routes>
      <ViewToggle />
      {showChat && <ChatWidget />}
      <TeamChatWidget />
    </InternalChatProvider>
    </ConfirmProvider>
    </ToastProvider>
    </ThemeProvider>
  );
}

export default App;